import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, XCircle, Eye, EyeOff, Shield } from 'lucide-react';
import { GuestAnomaly } from '../../types/guest';
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface AnomaliesTabProps {
  anomalies: GuestAnomaly[];
  onResolve: (anomalyId: string, resolution: string) => Promise<void>;
  onIgnore: (anomalyId: string, reason: string) => Promise<void>;
  loading?: boolean;
}

/**
 * AnomaliesTab - Composant pour afficher et gérer les anomalies d'un invité
 * Requirement 2.5: Affichage anomalies avec sévérité, statut et actions
 */
const AnomaliesTab: React.FC<AnomaliesTabProps> = ({
  anomalies = [],
  onResolve,
  onIgnore,
  loading = false
}) => {
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showResolutionModal, setShowResolutionModal] = useState<{ id: string; type: 'resolve' | 'ignore' } | null>(null);
  const [resolutionText, setResolutionText] = useState('');

  // Severity badge configuration
  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', label: 'Critique', icon: 'text-red-500' };
      case 'high':
        return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: 'Haute', icon: 'text-orange-500' };
      case 'medium':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', label: 'Moyenne', icon: 'text-yellow-500' };
      case 'low':
      default:
        return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', label: 'Basse', icon: 'text-blue-500' };
    }
  };

  // Status badge configuration
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'resolved':
        return { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Résolue' };
      case 'ignored':
        return { bg: 'bg-gray-100', text: 'text-gray-800', icon: EyeOff, label: 'Ignorée' };
      case 'open':
      default:
        return { bg: 'bg-red-100', text: 'text-red-800', icon: Clock, label: 'Ouverte' };
    }
  };

  const handleAction = async (type: 'resolve' | 'ignore') => {
    if (!showResolutionModal || !resolutionText.trim()) return;

    setProcessingId(showResolutionModal.id);
    try {
      if (type === 'resolve') {
        await onResolve(showResolutionModal.id, resolutionText);
      } else {
        await onIgnore(showResolutionModal.id, resolutionText);
      }
      setShowResolutionModal(null);
      setResolutionText('');
    } catch (error) {
      console.error('Erreur lors du traitement de l\'anomalie:', error);
    } finally {
      setProcessingId(null);
    }
  };

  // Sort anomalies: open first, then by severity (critical > high > medium > low), then by date
  const safeAnomalies = Array.isArray(anomalies) ? anomalies : [];
  const sortedAnomalies = [...safeAnomalies].sort((a, b) => {
    // Open anomalies first
    if (a.status === 'open' && b.status !== 'open') return -1;
    if (a.status !== 'open' && b.status === 'open') return 1;

    // Then by severity
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const severityDiff = (severityOrder[a.severity] || 3) - (severityOrder[b.severity] || 3);
    if (severityDiff !== 0) return severityDiff;

    // Then by date (most recent first)
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Chargement des anomalies...</span>
      </div>
    );
  }

  // Empty state
  if (safeAnomalies.length === 0) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune anomalie</h3>
        <p className="text-gray-600">Cet invité n'a aucune anomalie détectée.</p>
      </div>
    );
  }

  // Count open anomalies
  const openCount = safeAnomalies.filter(a => a.status === 'open').length;

  return (
    <div className="space-y-4">
      {/* Summary header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Anomalies ({safeAnomalies.length})
        </h3>
        {openCount > 0 && (
          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
            {openCount} ouverte{openCount > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Anomalies list */}
      <div className="space-y-3">
        {sortedAnomalies.map((anomaly) => {
          const severityConfig = getSeverityConfig(anomaly.severity);
          const statusConfig = getStatusConfig(anomaly.status);
          const StatusIcon = statusConfig.icon;
          const isProcessing = processingId === anomaly.id;

          return (
            <div 
              key={anomaly.id} 
              className={`border rounded-lg p-4 transition-colors ${
                anomaly.status === 'open' 
                  ? `border-l-4 ${severityConfig.border} bg-white` 
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 flex-1">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${severityConfig.icon}`} />
                  <div className="flex-1 min-w-0">
                    {/* Title and badges */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h4 className="font-medium text-gray-900">{anomaly.title}</h4>
                      {/* Severity badge */}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${severityConfig.bg} ${severityConfig.text}`}>
                        {severityConfig.label}
                      </span>
                      {/* Status badge */}
                      <span className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.text}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mt-1">{anomaly.description}</p>

                    {/* Type */}
                    <p className="text-xs text-gray-500 mt-2">
                      Type: <span className="font-medium">{anomaly.type}</span>
                    </p>

                    {/* Dates */}
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span>
                        Créée {formatDistanceToNow(new Date(anomaly.createdAt), { addSuffix: true, locale: fr })}
                        {' '}({format(new Date(anomaly.createdAt), 'dd/MM/yyyy à HH:mm', { locale: fr })})
                      </span>
                      {anomaly.resolvedAt && (
                        <span className="text-green-600">
                          Résolue le {format(new Date(anomaly.resolvedAt), 'dd/MM/yyyy à HH:mm', { locale: fr })}
                          {anomaly.resolvedBy && ` par ${anomaly.resolvedBy}`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions - only for open anomalies */}
                {anomaly.status === 'open' && (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => {
                        setShowResolutionModal({ id: anomaly.id, type: 'resolve' });
                        setResolutionText('');
                      }}
                      disabled={isProcessing}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Résoudre
                    </button>
                    <button
                      onClick={() => {
                        setShowResolutionModal({ id: anomaly.id, type: 'ignore' });
                        setResolutionText('');
                      }}
                      disabled={isProcessing}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <EyeOff className="w-4 h-4" />
                      Ignorer
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Resolution/Ignore Modal */}
      {showResolutionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {showResolutionModal.type === 'resolve' ? 'Résoudre l\'anomalie' : 'Ignorer l\'anomalie'}
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {showResolutionModal.type === 'resolve' 
                  ? 'Raison de la résolution:' 
                  : 'Raison de l\'ignorance:'}
              </label>
              <textarea
                value={resolutionText}
                onChange={(e) => setResolutionText(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={3}
                placeholder={showResolutionModal.type === 'resolve' 
                  ? 'Décrivez comment l\'anomalie a été résolue...'
                  : 'Expliquez pourquoi cette anomalie peut être ignorée...'}
                autoFocus
              />
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowResolutionModal(null);
                  setResolutionText('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleAction(showResolutionModal.type)}
                disabled={!resolutionText.trim() || processingId !== null}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  showResolutionModal.type === 'resolve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {processingId ? 'Traitement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnomaliesTab;
